import React from 'react';
import { Wand2, Plus, Trash2, Printer, Clock, Loader2 } from 'lucide-react';
import { DrillCard } from './DrillCard';
import { toast } from 'react-hot-toast';
import type { Drill, Coach, Team, Practice } from '../../types';

interface DrillListProps {  drills: Drill[];  coaches: Coach[];  drillCoaches: Record<string, string[]>;  selectedPractice: Practice | null;  favoriteStepIds: string[];  isMobileView: boolean;  timeFormat: '12h' | '24h';  isGenerating: boolean;  currentTeam: Team | null;  currentDrillIndex: number | null;  warningDrillIndex: number | null;    onAddDrill: () => void;  onEditDrill: (drill: Drill) => Promise<void>;  onDeleteDrill: (drillId: string, drillName?: string) => void;  onDeletePositionGroup: (drillId: string, groupName: string) => Promise<void>;  onToggleFavorite: (stepId: string) => void;  onPrint: () => void;  onToggleTimeFormat: () => void;  onGeneratePlan: () => void;  onClearDrills: () => void;  saveDrillOrder: (drills: Drill[]) => void;  onCoachChange: (drillId: string, groupName: string, coachId: string, action: 'add' | 'remove' | 'clear') => void;  isLoading: boolean; }

export function DrillList({  drills,  coaches,  drillCoaches,  selectedPractice,  favoriteStepIds,  isMobileView,  timeFormat,  isGenerating,  currentTeam,  currentDrillIndex,  warningDrillIndex,  onAddDrill,  onEditDrill,  onDeleteDrill,  onDeletePositionGroup,  onToggleFavorite,  onPrint,  onToggleTimeFormat,  onGeneratePlan,  onClearDrills,  saveDrillOrder,  onCoachChange,  isLoading}: DrillListProps) {

  // Move a drill up in the order
  const handleMoveDrillUp = (index: number) => {
    if (index === 0) return; // Already at the top

    const newDrills = [...drills];
    const drillToMove = newDrills[index];
    newDrills[index] = newDrills[index - 1];
    newDrills[index - 1] = drillToMove;

    saveDrillOrder(newDrills);
  };

  // Move a drill down in the order
  const handleMoveDrillDown = (index: number) => {
    if (index === drills.length - 1) return; // Already at the bottom

    const newDrills = [...drills];
    const drillToMove = newDrills[index];
    newDrills[index] = newDrills[index + 1];
    newDrills[index + 1] = drillToMove;

    saveDrillOrder(newDrills);
  };

  // Empty state component when no drills are present
  if (drills.length === 0 && !isGenerating && !isLoading) {
    return (
      <div className="text-center py-12 bg-white rounded-lg shadow-md">
        <h3 className="text-xl font-semibold mb-4">Ready to Create an Amazing Practice?</h3>
        <p className="text-gray-600 mb-6 max-w-md mx-auto">
          No drills yet? No problem! Let our AI coach help you create a winning practice plan in seconds. Just click below and watch the magic happen!
        </p>
        <div className="flex flex-col sm:flex-row justify-center gap-3">
          <button
            onClick={onGeneratePlan}
            className="btn-ai w-full max-w-[280px] flex items-center justify-center gap-2 mx-auto"
            disabled={isGenerating}
          >
            {isGenerating ? (
              <>
                <Loader2 size={20} className="animate-spin" />
                <span className="whitespace-nowrap">Generating...</span>
              </>
            ) : (
              <>
                <Wand2 size={20} />
                <span className="whitespace-nowrap">Generate Practice Plan</span>
              </>
            )}
          </button>
          <button
            onClick={onAddDrill}
            className="btn-primary w-full max-w-[280px] flex items-center justify-center gap-2 mx-auto"
            aria-label="Add Drill"
          >
            <Plus size={20} />
            <span className="whitespace-nowrap">Add Drill</span>
          </button>
        </div>
      </div>
    );
  }

  // Header Utility Bar - Only show on desktop
  const DesktopUtilityBar = () => {
    if (isMobileView) return null;
    
    return (
      <div className="hidden md:flex justify-between items-center mb-6 mt-6">
        <div className="flex gap-2">
          <button
            onClick={onToggleTimeFormat}
            className="btn-secondary flex items-center gap-2"
            title="Toggle Time Format"
          >
            <Clock size={20} className="mr-1" />
            {timeFormat === '12h' ? '24h' : '12h'}
          </button>
          <button
            onClick={onPrint}
            className="btn-secondary flex items-center gap-2"
            title="Print"
          >
            <Printer size={20} className="mr-1" />
          </button>
        </div>
        <div className="flex gap-2">
          <button
            onClick={onGeneratePlan}
            className="btn-ai flex items-center gap-2"
            disabled={isGenerating}
          >
            {isGenerating ? (
              <>
                <Loader2 size={20} className="animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Wand2 size={20} />
                Generate Plan
              </>
            )}
          </button>
          <button
            onClick={onAddDrill}
            className="btn-primary flex items-center gap-2"
          >
            <Plus size={20} />
            Add Drill
          </button>
          <button
            onClick={onClearDrills}
            className="btn-danger flex items-center gap-2"
          >
            <Trash2 size={20} />
            Clear Drills
          </button>
        </div>
      </div>
    );
  };

  // Calculate start and end minutes for each drill
  const drillsWithTiming = drills.map((drill, index) => {
    const startMinutes = index === 0 ? 0 : drills.slice(0, index).reduce((sum, d) => sum + d.duration, 0);
    const endMinutes = startMinutes + drill.duration;
    const isCurrent = index === currentDrillIndex;
    const isWarning = index === warningDrillIndex && !isCurrent;
    
    return {
      drill,
      index,
      startMinutes,
      endMinutes,
      isCurrent,
      isWarning
    };
  });

  return (
    <div className="mt-6">
      <DesktopUtilityBar />
      
      <div className="space-y-4">
        {drillsWithTiming.map(({ drill, index, startMinutes, endMinutes, isCurrent, isWarning }) => (
          <DrillCard
            key={`drill-${drill.id}-${index}`}
            drill={drill}
            index={index}
            isCurrent={isCurrent}
            isWarning={isWarning}
            startMinutes={startMinutes}
            endMinutes={endMinutes}
            startTime={selectedPractice?.start_time || ''}
            timeFormat={timeFormat}
            coaches={coaches}
            drillCoaches={drillCoaches}
            favoriteStepIds={favoriteStepIds}
            onMoveDrillUp={handleMoveDrillUp}
            onMoveDrillDown={handleMoveDrillDown}
            onEditDrill={onEditDrill}
            onDeleteDrill={onDeleteDrill}
            onDeletePositionGroup={onDeletePositionGroup}
            onToggleFavorite={onToggleFavorite}
            onCoachChange={onCoachChange}
            totalDrills={drills.length}
            currentTeam={currentTeam}
          />
        ))}
      </div>
    </div>
  );
}

export default DrillList; 